import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogContent, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { postVisual } from "../api";
import { VisualImage } from "../types";
import { useToast } from "../../../components/Toast";

interface MetaphorDeckToolProps {
  patientId: string;
  sessionId?: string;
  getToken: () => Promise<string | null>;
}

const COUNTS = [2, 3, 4, 5, 6, 7, 8];

/* Projective metaphor deck: N distinct symbolic images from one theme;
   the client picks the card that resonates. */
export function MetaphorDeckTool({ patientId, sessionId, getToken }: MetaphorDeckToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [theme, setTheme] = useState("");
  const [count, setCount] = useState(6);
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState<VisualImage[]>([]);
  const [presented, setPresented] = useState<VisualImage | null>(null);

  const handleGenerate = async () => {
    try {
      setBusy(true);
      const res = await postVisual(getToken, {
        patient_id: patientId, session_id: sessionId, kind: "deck", prompt: theme, count,
      });
      setCards(res.images);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg.includes("image_policy_declined") ? t("experiential.policyDeclined") : t("experiential.genError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <TextField
        label={t("experiential.deckTheme")}
        multiline
        minRows={2}
        fullWidth
        size="small"
        value={theme}
        onChange={(e) => setTheme(e.target.value)}
        sx={{ mb: 2 }}
      />
      <div className="exp-chair-controls">
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>{t("experiential.deckCount")}</InputLabel>
          <Select value={count} label={t("experiential.deckCount")} onChange={(e) => setCount(Number(e.target.value))}>
            {COUNTS.map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" size="small" onClick={handleGenerate} disabled={busy || !theme.trim()}>
          {t("experiential.generateDeck")}
        </Button>
      </div>

      {cards.length > 0 && (
        <div className="exp-deck-grid">
          {cards.map((card) => (
            <button
              key={card.id}
              type="button"
              className="exp-deck-card"
              onClick={() => setPresented(card)}
              aria-label={`${t("experiential.card")} ${card.id}`}
            >
              <img src={card.url} alt={`${t("experiential.card")} ${card.id}`} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={presented !== null} onClose={() => setPresented(null)} maxWidth="xl" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {presented && <img src={presented.url} alt={t("experiential.card")} className="exp-present-img" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
